import type { AxiosInstance } from 'axios';

export type FileVisibility = 'PUBLIC' | 'PRIVATE';

export interface UploadedFile {
  id: string;
  bucket: string;
  key: string;
  name: string;
  contentType: string;
  sizeBytes: number;
  visibility: FileVisibility;
  url: string | null;
  createdAt: string;
}

export interface UploadedFileListResponse {
  data: UploadedFile[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

export function fileEndpoints(http: AxiosInstance) {
  return {
    getUploadSignature: (params?: { folder?: string; type?: 'upload' | 'authenticated' }) =>
      http
        .get<{
          signature: string;
          timestamp: number;
          apiKey: string;
          cloudName: string;
          folder: string;
          /**
           * Cloudinary delivery type. `'authenticated'` means the asset is
           * NOT publicly addressable — uploads must POST to the
           * `authenticated` endpoint and reads require a signed delivery URL.
           */
          type: 'upload' | 'authenticated';
        }>('/files/signature', { params })
        .then((r) => r.data),
    getSignedUrl: (fileId: string) =>
      http
        .get<{ url: string; expiresInSec: number }>(`/files/${fileId}/signed-url`)
        .then((r) => r.data),
    list: (params?: { q?: string; sort?: 'newest' | 'oldest' | 'name' | 'size'; page?: number }) =>
      http.get<UploadedFileListResponse>('/admin/files', { params }).then((r) => r.data),
    get: (fileId: string) => http.get<UploadedFile>(`/admin/files/${fileId}`).then((r) => r.data),
    /** Admin-only registration (uses `file.write` permission). */
    register: (body: {
      name: string;
      url: string;
      contentType?: string;
      sizeBytes?: number;
      visibility?: FileVisibility;
    }) => http.post<UploadedFile>('/admin/files', body).then((r) => r.data),
    /**
     * Self-service registration for an authenticated user (vendor, customer).
     * Use after a direct-to-Cloudinary upload to persist an internal FileObject
     * and obtain its id — required when downstream entities reference it
     * (e.g. KYC docs store the FileObject id, not the Cloudinary URL).
     */
    registerSelf: (body: {
      name: string;
      url: string;
      contentType?: string;
      sizeBytes?: number;
      visibility?: FileVisibility;
    }) => http.post<UploadedFile>('/files/register', body).then((r) => r.data),
    remove: (fileId: string) => http.delete(`/admin/files/${fileId}`).then((r) => r.data),
    bulkDelete: (ids: string[]) =>
      http
        .post<{
          successful: number;
          failed: number;
          errors: string[];
        }>('/admin/files/bulk-delete', { ids })
        .then((r) => r.data),
  };
}

export type FileEndpoints = ReturnType<typeof fileEndpoints>;
